declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * The AutoSpecData GA4 property the owner asked for. Pinned here so a
 * connector re-sync cannot silently point tracking at a different account.
 */
const SITE_GA_MEASUREMENT_ID = "G-4K0MN8B7V6";

export const GA_MEASUREMENT_ID = SITE_GA_MEASUREMENT_ID;

type QueuedEvent = { name: string; params: Record<string, unknown> };
const queue: QueuedEvent[] = [];
let initialized = false;

/**
 * Runs `fn` on the first real user interaction, or after `fallbackMs` of
 * inactivity. Keeps third-party JS out of the page-load window entirely, which
 * is what Lighthouse measures for Total Blocking Time.
 */
export function onFirstInteraction(fn: () => void, fallbackMs = 10000) {
  if (typeof window === "undefined") return () => {};
  let done = false;
  const events = ["pointerdown", "keydown", "touchstart", "scroll", "wheel"] as const;
  const run = () => {
    if (done) return;
    done = true;
    cleanup();
    fn();
  };
  const timer = window.setTimeout(run, fallbackMs);
  const cleanup = () => {
    window.clearTimeout(timer);
    events.forEach((e) => window.removeEventListener(e, run));
  };
  events.forEach((e) => window.addEventListener(e, run, { once: true, passive: true }));
  return cleanup;
}

export function initGoogleAnalytics(): boolean {
  if (!GA_MEASUREMENT_ID) return false;
  if (typeof document === "undefined") return false;
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`)) {
    return true;
  }

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  initialized = true;
  queue.splice(0).forEach(({ name, params }) => window.gtag!("event", name, params));
  return true;
}

export const CLARITY_PROJECT_ID = "y6o3b722eh";

/** Microsoft Clarity — injected lazily so it stays out of the load window. */
export function initClarity(): boolean {
  if (typeof document === "undefined") return false;
  if (document.querySelector(`script[src*="clarity.ms/tag/${CLARITY_PROJECT_ID}"]`)) return true;


  type ClarityFn = ((...args: unknown[]) => void) & { q: unknown[] };
  const w = window as unknown as Record<string, unknown>;
  if (!w["clarity"]) {
    const c = ((...args: unknown[]) => {
      c.q.push(args);
    }) as ClarityFn;
    c.q = [];
    w["clarity"] = c;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
  document.head.appendChild(script);
  return true;
}

function send(name: string, params: Record<string, unknown>) {
  if (!GA_MEASUREMENT_ID || typeof window === "undefined") return;
  if (!initialized || !window.gtag) {
    queue.push({ name, params });
    if (queue.length > 50) queue.shift();
    return;
  }
  window.gtag("event", name, params);
}

export function trackPageView(path: string) {
  if (typeof window === "undefined") return;
  send("page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
    send_to: GA_MEASUREMENT_ID,
  });
}

export function trackEvent(name: string, params?: Record<string, string | number | boolean>) {
  send(name, { send_to: GA_MEASUREMENT_ID, ...params });
}
